import * as React from 'react';
import ReactDOM from 'react-dom';

import classNames from 'classnames';
import type monaco from 'monaco-editor';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { CMLCodeEvaluatorV2, languageMapper } from 'bundles/cml';
import type { LanguageType } from 'bundles/cml';
import type { Props as CodeEvaluatorOutputProps } from 'bundles/code-evaluator/components/CodeEvaluatorOutput';
import TabTrappingMessage from 'bundles/code-evaluator/components/TabTrappingMessage';
import EvaluatorSummary from 'bundles/code-evaluator/models/EvaluatorSummary';
import { getSummary } from 'bundles/code-evaluator/utils/EvaluatorAPIUtils';
import { defineCustomTheme } from 'bundles/code-evaluator/utils/MonacoUtils';
import {
  registerLanguage,
  tokenizedLanguages,
} from 'bundles/code-evaluator/utils/language-definitions/registerLanguages';
import localStorageEx from 'bundles/common/utils/localStorageEx';

import _t from 'i18n!nls/cml';

import 'css!./__styles__/CodeBlock';

type Monaco = {
  Range: typeof monaco.Range;
};

const loadMonacoEditor = () => import('monaco-editor/esm/vs/editor/editor.main');

export interface CodeBlockV2Handle {
  runCode: () => void;
  resetCode: () => void;
}

export type Props = {
  codeLanguage: LanguageType;
  expression: string;
  evaluatorId?: string;
  onUpdate?: (answer: string) => void;
  useUserExpression?: boolean;
  hideCodeEvaluator?: boolean;
  className?: string;
  codeBlockId?: string;
  ariaLabel?: string;
  minLines?: number;
  maxLines?: number;
  onChange?: (value: string, event: monaco.editor.IModelContentChangedEvent) => void;
  onBlur?: (value: string) => void;
  readOnly?: boolean;
  isControlled?: boolean;
  monacoCreateOptions?: monaco.editor.IStandaloneEditorConstructionOptions;
  starterExpression?: string;
  hideActionButtons?: boolean;
  renderOutput?: (props: CodeEvaluatorOutputProps) => React.ReactNode;
};

type State = {
  prevHeight: number;
};

/**
 * @class CodeBlock
 *
 * CodeBlock based on monaco editor.
 *
 */
class CodeBlockV2 extends React.Component<Props, State> {
  static defaultProps = {
    expression: '',
    useUserExpression: true,
    hideCodeEvaluator: false,
    maxLines: 40,
    isControlled: false,
  };

  state = {
    prevHeight: 0,
  };

  monacoInstance: Monaco | null = null;

  componentDidUpdate(prevProps: Props) {
    if (this.props.isControlled && this.props.expression !== prevProps.expression) {
      this.monacoEditor?.setValue(this.props.expression);
    }
  }

  componentDidMount() {
    const {
      codeLanguage,
      evaluatorId,
      expression,
      onUpdate,
      useUserExpression,
      hideCodeEvaluator,
      readOnly,
      monacoCreateOptions,
      starterExpression,
    } = this.props;
    this.isECB = !!evaluatorId;
    const isReadOnly = typeof readOnly === 'boolean' ? readOnly : !this.isECB;
    const { container } = this;
    this.accessibilitySupport = localStorageEx.getItem('codeBlockAccessibilityEnabled', JSON.parse, 'auto');

    loadMonacoEditor().then((monacoInstance) => {
      this.monacoInstance = monacoInstance;
      const monacoEditorLanguage = languageMapper[codeLanguage] || codeLanguage;

      const customThemeName = 'vs-extended';
      const shouldUseCustomTheme = monacoEditorLanguage === languageMapper.sql;

      if (shouldUseCustomTheme) {
        defineCustomTheme(customThemeName, monacoInstance);
      }

      // check if language support does not come bundled with monaco editor
      if (monacoEditorLanguage in tokenizedLanguages) {
        // @ts-expect-error TODO: We already know that language is correct. I think it is better to move this check to registerLanguage
        registerLanguage(monacoInstance, monacoEditorLanguage);
      }

      const defaultEditorConfig: monaco.editor.IStandaloneEditorConstructionOptions = {
        language: monacoEditorLanguage,
        readOnly: isReadOnly,
        accessibilitySupport: this.accessibilitySupport,
        minimap: {
          enabled: false,
        },
        ...(shouldUseCustomTheme ? { theme: customThemeName } : {}),
      };

      if (evaluatorId) {
        // render CML Code Evaluator if it's an executable code block
        getSummary(evaluatorId)
          .then((response) => {
            if (container?.parentNode) {
              const evaluatorSummary = new EvaluatorSummary(response);
              const value: string = (useUserExpression && evaluatorSummary.latestUserExpression) || expression;
              this.monacoEditor = monacoInstance.editor.create(container, {
                ...defaultEditorConfig,
                ...monacoCreateOptions,
                value,
                scrollBeyondLastLine: false,
                glyphMargin: true, // Enable rendering of the glyph margin
              });

              if (onUpdate && value !== expression) {
                onUpdate(value);
              }

              if (hideCodeEvaluator) {
                return;
              }

              const evaluatorContainer = document.createElement('div');
              evaluatorContainer.setAttribute('class', 'cml-code-evaluator');
              container.parentNode.insertBefore(evaluatorContainer, container.nextSibling);
              ReactDOM.render(
                <CMLCodeEvaluatorV2
                  // componentRef is added by Retracked.createTrackedContainer
                  // @ts-expect-error
                  componentRef={this.cmlCodeEvaluatorRef}
                  monacoEditor={this.monacoEditor}
                  monacoInstance={monacoInstance}
                  evaluatorId={evaluatorId}
                  evaluatorSummary={evaluatorSummary}
                  onUpdate={onUpdate}
                  starterExpression={typeof starterExpression === 'string' ? starterExpression : expression}
                  hideActionButtons={this.props.hideActionButtons}
                  renderOutput={this.props.renderOutput}
                />,
                evaluatorContainer
              );
              this.updateEditorLayout();
              this.initializeMonacoChangeListener();
            }
          })
          .fail(() => {
            const errorContainer = document.createElement('div');
            errorContainer.setAttribute('class', 'color-warn-dark');
            errorContainer.innerHTML = _t('Error loading runnable code block');
            if (container?.parentNode) {
              container.parentNode.insertBefore(errorContainer, container.nextSibling);
            }
          });
      } else if (container) {
        this.monacoEditor = monacoInstance.editor.create(container, {
          ...defaultEditorConfig,
          ...monacoCreateOptions,
          value: expression,
        });
        this.updateEditorLayout();
        this.initializeMonacoChangeListener();
      }

      window.addEventListener('resize', this.updateDimensions);
      window.addEventListener('storage', this.initializeStorageListener);
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
    window.removeEventListener('storage', this.initializeStorageListener);
    this.editorDidChangeModelContentListener?.dispose();
    this.editorDidBlurModelContentListener?.dispose();
    this.editorDidChangeConfigurationListener?.dispose();
  }

  handleChange = (event: monaco.editor.IModelContentChangedEvent) => {
    const { monacoEditor } = this;
    const { onChange } = this.props;

    if (!monacoEditor) {
      return;
    }

    if (onChange && !this.isECB) {
      const value = monacoEditor.getValue();
      onChange(value, event);
    }

    this.updateEditorLayout();
  };

  handleBlur = () => {
    const { monacoEditor } = this;
    const { onBlur } = this.props;

    if (!monacoEditor) {
      return;
    }

    if (onBlur) {
      const value = monacoEditor.getValue();
      onBlur(value);
    }
  };

  assignRef = (component: HTMLElement | null) => {
    this.container = component;
  };

  initializeMonacoChangeListener() {
    const { monacoEditor } = this;

    if (!monacoEditor) {
      return;
    }

    this.editorDidChangeModelContentListener = monacoEditor.onDidChangeModelContent(this.handleChange);
    this.editorDidBlurModelContentListener = monacoEditor.onDidBlurEditorText?.(this.handleBlur);

    this.editorDidChangeConfigurationListener = monacoEditor.onDidChangeConfiguration((event) => {
      // check if the change occurred with the accessibilitySupport setting
      if (event.hasChanged(2)) {
        this.accessibilitySupport = this.accessibilitySupport === 'on' ? 'off' : 'on';
        localStorageEx.setItem('codeBlockAccessibilityEnabled', this.accessibilitySupport, JSON.stringify);
      }
    });
  }

  initializeStorageListener = (event: StorageEvent) => {
    const { monacoEditor } = this;

    if (!monacoEditor || event.key !== 'codeBlockAccessibilityEnabled') {
      return;
    }

    if (event.newValue) {
      const nextAccessibilitySetting = JSON.parse(event.newValue);
      monacoEditor.updateOptions({ accessibilitySupport: nextAccessibilitySetting });
    }
  };

  updateEditorLayout() {
    const { monacoEditor } = this;
    const { minLines, maxLines } = this.props;
    const { prevHeight } = this.state;
    const editorElement = monacoEditor?.getDomNode();

    if (!monacoEditor || !editorElement) {
      return;
    }

    // TODO(wbowers): Using the `monaco.editor.EditorOption.lineHeight` constant here breaks the app,
    // giving `[error] InherentError: { Error: Cannot find module 'monaco-editor'`. Seems like
    // something is calling this function before the module is loaded.
    const lineHeight = monacoEditor.getOption(49);
    const lineCount = monacoEditor.getModel()?.getLineCount() || 1; // get the number of lines of code in the editor
    const paddedLineCount = lineCount + 1; // Makes room for an area at the bottom of the editor.
    const actualMinLines = this.isECB ? minLines || 5 : minLines;
    const requestedLineCount = Math.min(
      Math.max(paddedLineCount, actualMinLines || paddedLineCount),
      maxLines || paddedLineCount
    );

    const height = requestedLineCount * lineHeight;

    if (prevHeight !== height) {
      this.setState({ prevHeight: height });
      editorElement.style.height = `${height}px`; // update monaco container element height
      monacoEditor.layout(); // Instruct monaco editor to remeasure its container
    }
  }

  updateDimensions = () => {
    this.monacoEditor?.layout();
  };

  container: HTMLElement | null = null;

  isECB = false;

  accessibilitySupport: 'auto' | 'on' | 'off' = 'auto';

  monacoEditor: monaco.editor.IStandaloneCodeEditor | null = null;

  editorDidChangeModelContentListener: monaco.IDisposable | null = null;

  editorDidBlurModelContentListener: monaco.IDisposable | null = null;

  editorDidChangeConfigurationListener: monaco.IDisposable | null = null;

  private cmlCodeEvaluatorRef = React.createRef<CodeBlockV2Handle>();

  public runCode = () => {
    this.cmlCodeEvaluatorRef.current?.runCode();
  };

  public resetCode = () => {
    this.cmlCodeEvaluatorRef.current?.resetCode();
  };

  render() {
    const { className, codeBlockId, ariaLabel, hideActionButtons, renderOutput } = this.props;
    return (
      <React.Fragment>
        <div
          ref={this.assignRef}
          id={codeBlockId}
          className={classNames('rc-CodeBlock', 'rc-CodeBlockV2', className)}
          role="region"
          aria-label={ariaLabel || _t('Code block')}
          tabIndex={-1}
          dir="ltr"
        />
        {this.isECB && this.monacoEditor && this.props.evaluatorId && renderOutput && (
          <CMLCodeEvaluatorV2
            // componentRef is added by Retracked.createTrackedContainer
            // @ts-expect-error
            componentRef={this.cmlCodeEvaluatorRef}
            monacoEditor={this.monacoEditor}
            monacoInstance={this.monacoInstance as Monaco}
            evaluatorId={this.props.evaluatorId!}
            evaluatorSummary={new EvaluatorSummary({})}
            onUpdate={this.props.onUpdate}
            starterExpression={this.props.starterExpression || ''}
            hideActionButtons={hideActionButtons}
            renderOutput={renderOutput}
          />
        )}
        <TabTrappingMessage editor={this.monacoEditor} codeBlockId={codeBlockId} />
      </React.Fragment>
    );
  }
}

export const ForwardedCodeBlockV2 = React.forwardRef<CodeBlockV2Handle, Props>((props, ref) => {
  const innerRef = React.useRef<CodeBlockV2>(null);
  React.useImperativeHandle(ref, () => ({
    runCode: () => innerRef.current?.runCode(),
    resetCode: () => innerRef.current?.resetCode(),
  }));
  return <CodeBlockV2 {...props} ref={innerRef} />;
});

export default CodeBlockV2;
